import { colors } from "@zfp-akademie-a-s/zfp-design";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { formatCurrency, formatCurrencyShortText } from "Infrastructure/Utils/CurrencyUtils";
import { calculateMonthlyEffectiveCompoundRate } from "./CommonChartUtils";

type GenerateMortgageRepaymentChartDataParams = {
  readonly remainingPrincipal: number;
  readonly remainingYears: number;
  readonly monthlyContributionAmount: number;
  readonly inflationRate: number;
  readonly interestRate: number;
  readonly loanInterestRate: number;
  readonly loanPaymentAmount: number;
};

type GenerateRepaymentDataParams = Pick<
  GenerateMortgageRepaymentChartDataParams,
  "remainingPrincipal" | "loanPaymentAmount" | "remainingYears" | "loanInterestRate"
>;

type GenerateInvestmentDataParams = Pick<
  GenerateMortgageRepaymentChartDataParams,
  "inflationRate" | "interestRate" | "monthlyContributionAmount" | "remainingYears"
>;

type CalculateMonthlyPaymentParams = Pick<
  GenerateMortgageRepaymentChartDataParams,
  "remainingPrincipal" | "remainingYears" | "inflationRate" | "loanInterestRate"
>;

type CalculateMonthlyContributionParams = Pick<GenerateMortgageRepaymentChartDataParams, "inflationRate" | "interestRate"> & {
  readonly targetAmount: number;
  readonly desiredRemainingYears: number;
};

type CalculateTargetAmountParams = Pick<
  GenerateMortgageRepaymentChartDataParams,
  "remainingPrincipal" | "loanInterestRate" | "loanPaymentAmount"
> & {
  readonly desiredRemainingYears: number;
};

type MortgageRepaymentChartData = {
  readonly accumulationPhaseData: number[];
  readonly repaymentPhaseData: number[];
  readonly repaymentIndex: number;
};

type GetAnnotationsParams = {
  readonly targetAmount: number;
  readonly repaymentIndex: number;
  readonly remainingPrincipal: number;
  readonly maxAmount: number;
};

export const generateMortgageRepaymentChartData = ({
  monthlyContributionAmount,
  loanInterestRate,
  interestRate,
  loanPaymentAmount,
  inflationRate,
  remainingPrincipal,
  remainingYears,
}: GenerateMortgageRepaymentChartDataParams): MortgageRepaymentChartData => {
  const repayment = _generateRepaymentData({
    loanPaymentAmount,
    loanInterestRate,
    remainingPrincipal,
    remainingYears,
  });

  const accumulation = _generateInvestmentData({
    monthlyContributionAmount,
    inflationRate,
    interestRate,
    remainingYears,
  });

  const padding = new Array(Math.floor(accumulation.length / 7)).fill(Number.NaN);
  const accumulationPhaseData = padding.concat(accumulation);
  const repaymentPhaseData = padding.concat(repayment);

  const repaymentIndex = accumulationPhaseData.findIndex((accumulationValue, index) => {
    const repaymentValue = repaymentPhaseData[index];
    return repaymentValue <= accumulationValue + accumulationValue * 0.005;
  });

  return {
    accumulationPhaseData,
    repaymentPhaseData,
    repaymentIndex,
  };
};

export const getAnnotations = ({ repaymentIndex, targetAmount, remainingPrincipal, maxAmount }: GetAnnotationsParams) => {
  const commonLineProps = {
    type: "line" as const,
    borderColor: "rgba(0, 0, 0, 0.05)",
    borderWidth: 0.8,
    borderDash: [6, 4],
  };

  const commonLabelProps = {
    display: true,
    backgroundColor: "transparent",
    color: colors.textSecondary,
    position: "start" as const,
    yAdjust: 10,
    xAdjust: -4,
    font: {
      weight: 400,
      size: 10,
    },
  };

  return {
    earlyRepaymentLine: {
      ...commonLineProps,
      borderDash: [3, 4],
      borderColor: colors.chartPositiveLight,
      xMin: repaymentIndex,
      xMax: repaymentIndex,
      yMax: remainingPrincipal,
      yMin: 0,
    },
    remainingPrincipalLine: {
      label: {
        ...commonLabelProps,
        content: formatCurrencyShortText(remainingPrincipal),
        color: colors.chartNeutral,
      },
      ...commonLineProps,
      borderColor: colors.chartNeutral,
      xMin: 0,
      xMax: Number.MAX_VALUE,
      yMax: remainingPrincipal,
      yMin: remainingPrincipal,
    },
    targetAmountLine: {
      label: {
        ...commonLabelProps,
        content: formatCurrencyShortText(targetAmount),
        color: colors.chartPositiveLight,
      },
      ...commonLineProps,
      borderColor: colors.chartPositiveLight,
      xMin: 0,
      xMax: Number.MAX_VALUE,
      yMax: targetAmount,
      yMin: targetAmount,
    },
    maxAmountLine: {
      label: {
        ...commonLabelProps,
        content: formatCurrencyShortText(maxAmount),
        color: colors.chartPositiveLight,
      },
      ...commonLineProps,
      borderColor: colors.chartPositiveLight,
      xMin: 0,
      xMax: Number.MAX_VALUE,
      yMax: maxAmount,
      yMin: maxAmount,
    },
  };
};

export const calculateMonthlyPayment = ({ remainingPrincipal, remainingYears, loanInterestRate }: CalculateMonthlyPaymentParams) => {
  const monthlyCompoundRate = calculateMonthlyEffectiveCompoundRate(loanInterestRate, 0);
  return (
    remainingPrincipal *
    ((monthlyCompoundRate * (1 + monthlyCompoundRate) ** (remainingYears * 12)) / ((1 + monthlyCompoundRate) ** (remainingYears * 12) - 1))
  );
};

export const calculateMonthlyContribution = ({
  desiredRemainingYears,
  inflationRate,
  interestRate,
  targetAmount,
}: CalculateMonthlyContributionParams) => {
  const monthlyCompoundRate = calculateMonthlyEffectiveCompoundRate(interestRate, inflationRate);

  return targetAmount / ((-1 + (1 + monthlyCompoundRate) ** (desiredRemainingYears * 12)) / monthlyCompoundRate);
};

export const calculateTargetAmount = ({
  desiredRemainingYears,
  remainingPrincipal,
  loanInterestRate,
  loanPaymentAmount,
}: CalculateTargetAmountParams) => {
  const monthlyCompoundRate = calculateMonthlyEffectiveCompoundRate(loanInterestRate, 0);
  return (
    remainingPrincipal * (1 + monthlyCompoundRate) ** (desiredRemainingYears * 12) -
    loanPaymentAmount * (((1 + monthlyCompoundRate) ** (desiredRemainingYears * 12) - 1) / monthlyCompoundRate)
  );
};

const _generateInvestmentData = ({
  remainingYears,
  interestRate,
  inflationRate,
  monthlyContributionAmount,
}: GenerateInvestmentDataParams) => {
  const result: number[] = [];
  const monthlyCompoundRate = calculateMonthlyEffectiveCompoundRate(interestRate, inflationRate);

  for (let i = 0; i < remainingYears + 1; i++) {
    const formula = (monthlyContributionAmount * ((1 + monthlyCompoundRate) ** (i * 12) - 1)) / monthlyCompoundRate;
    result.push(formula);
  }

  return result;
};

const _generateRepaymentData = ({
  remainingYears,
  remainingPrincipal,
  loanInterestRate,
  loanPaymentAmount,
}: GenerateRepaymentDataParams) => {
  const result: number[] = [];
  const monthlyCompoundRate = calculateMonthlyEffectiveCompoundRate(loanInterestRate, 0);

  for (let i = 0; i < remainingYears + 1; i++) {
    const formula =
      remainingPrincipal * (1 + monthlyCompoundRate) ** (i * 12) -
      loanPaymentAmount * (((1 + monthlyCompoundRate) ** (i * 12) - 1) / monthlyCompoundRate);
    result.push(formula);
  }

  return result;
};
